import React, { useEffect, useState } from 'react';
import ConfirmSheet from './ConfirmSheet';
import {
  registerDialogHost,
  settleDialog,
  type DialogRequest,
} from '../../services/dialog';

/**
 * Every confirmation and warning raised from outside a screen, drawn once.
 *
 * Mounted a single time at the root of `App.tsx`. It renders nothing until
 * `confirm()` or `notify()` is called from `services/dialog` - see the long
 * note there for why that side is imperative and this side is not.
 *
 * ---------------------------------------------------------------------------
 * IT IS A `ConfirmSheet`, DELIBERATELY
 * ---------------------------------------------------------------------------
 * Not a new component. Five screens already ask "are you sure" through
 * `ConfirmSheet`, and it is the thing that keeps the destructive button on the
 * right and labelled with a verb. A second dialog drawn a second way would
 * mean the same question looks like two different products depending on
 * whether the code asking it happened to live in a component - which is
 * exactly the inconsistency this whole change is undoing.
 *
 * What it replaces is the OS `Alert`, which looked like neither: grey and
 * squared on one Android skin, rounded and blue on another, and nothing like
 * Homingo on either.
 */
function DialogHost() {
  const [request, setRequest] = useState<
    (DialogRequest & { id: number }) | null
  >(null);

  useEffect(() => registerDialogHost(setRequest), []);

  const close = (confirmed: boolean) => {
    setRequest(null);
    settleDialog(confirmed);
  };

  return (
    <ConfirmSheet
      visible={!!request}
      title={request?.title ?? ''}
      body={request?.message ?? ''}
      confirmLabel={request?.confirmLabel ?? 'OK'}
      /*
       * `null` reaches `ConfirmSheet` as "this only reports" and it drops the
       * second button - the same meaning it carries in `DialogRequest`, which
       * is why the two are passed straight through rather than translated.
       */
      cancelLabel={request?.cancelLabel}
      tone={request?.tone ?? 'danger'}
      onConfirm={() => close(true)}
      onClose={() => close(false)}
    />
  );
}

export default DialogHost;
